import { toast } from '@ixigo/iui/Toast';
import { BottomsheetExtended } from '@repo/shared/components';
import { useParams, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@/lib/navigation';
import {
	type CancellationReason,
	initiateCancellation,
} from '../../-api/cancellation';
import { CancellationErrorView } from './error-view';
import { useCancellationData } from './hook';
import { CancellationDetailLoader, SubmissionLoader } from './loading-screens';
import { CancellationFlow } from './main-flow';
import { CancellationFailure, CancellationSuccess } from './result-screens';

interface CancellationProps {
	open: boolean;
	onModalClosed?: () => void;
	onRouterOperationStart?: () => void;
}

export function Cancellation({
	open,
	onModalClosed,
	onRouterOperationStart,
}: CancellationProps) {
	const [showSubmissionFailure, setShowSubmissionFailure] = useState(false);
	const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
	const [submissionLoading, setSubmissionLoading] = useState(false);
	const [selectedReason, setSelectedReason] =
		useState<CancellationReason | null>(null);
	const [internalOpen, setInternalOpen] = useState(false);
	const exitCallbackRef = useRef<() => void>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const router = useRouter();
	const navigate = useNavigate();
	const { bookingId } = useParams({ from: '/_auth/trips/$bookingId/' });
	const cancellationDataState = useCancellationData(bookingId, open);
	const abortController = useRef<AbortController | null>(null);

	// Sync internal state with external open prop
	useEffect(() => {
		setInternalOpen(open);
	}, [open]);

	const handleBack = () => {
		setInternalOpen(false); // Close the modal internally, let transition complete
		abortController.current?.abort();
	};

	const handleErrorAction = () => {
		handleBack();
		const errorType =
			cancellationDataState.status === 'error'
				? (cancellationDataState.error?.type ?? 'GENERIC_ERROR')
				: 'GENERIC_ERROR';

		if (errorType === 'RETURN_HOME' || errorType === 'GENERIC_ERROR') {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			exitCallbackRef.current = () => {
				navigate({
					to: '/',
					viewTransition: { types: ['slide-right'] },
				});
			};
		} else if (errorType === 'REFRESH') {
			exitCallbackRef.current = () => {
				navigate({ to: '.', replace: true });
			};
		}
	};

	const handleSubmit = async () => {
		if (!selectedReason) {
			toast.error('Please select a reason for cancellation', {
				duration: 2000,
				containerStyle: {
					bottom: '90px',
				},
			});
			return;
		}
		const startTime = performance.now();
		setShowSubmissionFailure(false);
		setSubmissionLoading(true);
		abortController.current = new AbortController();
		const initiateCancellationResponse = await initiateCancellation(
			bookingId,
			selectedReason,
			{
				signal: abortController.current.signal,
			},
		);
		if ('data' in initiateCancellationResponse) {
			onRouterOperationStart?.();
			await router.invalidate();
			setSubmissionLoading(false);
			setShowSubmissionSuccess(true);
			exitCallbackRef.current = () => {
				window.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			};
			return;
		}
		const endTime = performance.now();
		const minTimeToShowLoader = 600;
		if (endTime - startTime > minTimeToShowLoader) {
			setSubmissionLoading(false);
			setShowSubmissionFailure(true);
		} else {
			timeoutRef.current = setTimeout(
				() => {
					setSubmissionLoading(false);
					setShowSubmissionFailure(true);
				},
				minTimeToShowLoader - (endTime - startTime),
			);
		}
		exitCallbackRef.current = () => {
			setSubmissionLoading(false);
			setShowSubmissionFailure(false);
		};
	};

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const getContentState = ():
		| 'failure'
		| 'submitting'
		| 'submission-success'
		| 'loading'
		| 'error'
		| 'success'
		| 'idle' => {
		if (showSubmissionFailure) return 'failure';
		if (submissionLoading) return 'submitting';
		if (showSubmissionSuccess) return 'submission-success';
		return cancellationDataState.status;
	};

	const renderContent = () => {
		const contentState = getContentState();

		const contentMap: Record<string, React.ReactNode> = {
			failure: (
				<CancellationFailure
					onClose={handleBack}
					onGetHelp={() => {
						// Todo: add redirection to support page
					}}
					onTryAgain={handleSubmit}
				/>
			),
			submitting: <SubmissionLoader />,
			loading: <CancellationDetailLoader onClose={handleBack} />,
			error:
				cancellationDataState.status === 'error' ? (
					<CancellationErrorView
						error={cancellationDataState.error}
						onAction={handleErrorAction}
					/>
				) : null,
			'submission-success': <CancellationSuccess onClose={handleBack} />,
			success:
				cancellationDataState.status === 'success' &&
				cancellationDataState.data ? (
					<CancellationFlow
						onClose={handleBack}
						onConfirm={handleSubmit}
						selectedReason={selectedReason}
						onSelectionChange={setSelectedReason}
						cancellationInfo={cancellationDataState.data}
					/>
				) : null,
		};

		return contentMap[contentState];
	};

	return (
		<BottomsheetExtended
			open={internalOpen}
			onClose={submissionLoading ? undefined : handleBack}
			showCloseButton={false}
			TransitionProps={{
				onExited: () => {
					if (exitCallbackRef.current) {
						exitCallbackRef.current();
						exitCallbackRef.current = null;
					}
					// Notify parent that modal has fully closed
					onModalClosed?.();
				},
			}}
		>
			{renderContent()}
		</BottomsheetExtended>
	);
}
